import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Flame } from "lucide-react";
import api from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ loading: true, paid: false, error: null, planId: null });
  const pollingRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus({ loading: false, paid: false, error: "Session ID mancante", planId: null }); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus({ loading: false, paid: true, error: null, planId: data.plan_id });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus({ loading: false, paid: false, error: "Pagamento non completato", planId: null });
          return;
        }
        pollingRef.current += 1;
        if (pollingRef.current > 20) {
          setStatus({ loading: false, paid: false, error: "Timeout verifica pagamento — controlla email o dashboard", planId: null });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus({ loading: false, paid: false, error: "Errore verifica pagamento", planId: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0B0A08] text-[#FBF9F5] grid place-items-center p-6 relative overflow-hidden">
      <div className="fixed -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#3B503A]/30 blur-[120px] pointer-events-none"/>
      <div className="fixed -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#D99B26]/20 blur-[120px] pointer-events-none"/>
      <div className="relative max-w-md w-full text-center">
        {status.loading ? (
          <>
            <Loader2 size={56} className="mx-auto animate-spin text-[#D99B26]"/>
            <h1 className="mt-6 font-serif-display text-3xl font-black">Verifica pagamento...</h1>
            <p className="mt-2 text-white/70">Stripe sta confermando la tua sottoscrizione.</p>
          </>
        ) : status.paid ? (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#D99B26] to-[#C85A32] grid place-items-center shadow-2xl shadow-[#C85A32]/40">
              <CheckCircle2 size={40} className="text-white"/>
            </div>
            <h1 className="mt-6 font-serif-display text-4xl font-black">Attivato! 🎉</h1>
            <div className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#3B503A]/40 text-[#EAF0E9] text-[11px] font-black uppercase tracking-widest">
              <Flame size={11}/> 3 giorni gratis · disdici quando vuoi
            </div>
            <p className="mt-4 text-white/80 leading-relaxed">Il tuo piano <b className="text-[#D99B26]">{status.planId === "plan_all_in" ? "All In" : "Menu AI"}</b> è attivo. Le funzioni sono già sbloccate sul tuo dashboard.</p>
            <button data-testid="go-to-dashboard-btn" onClick={() => navigate("/dashboard")} className="mt-6 w-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] hover:brightness-110 text-white rounded-full py-4 font-black">
              Vai al Dashboard →
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-red-900/60 grid place-items-center">
              <span className="text-3xl">⚠️</span>
            </div>
            <h1 className="mt-6 font-serif-display text-3xl font-black">Verifica non riuscita</h1>
            <p className="mt-3 text-white/70">{status.error || "Non abbiamo potuto confermare il pagamento. Riprova dal dashboard."}</p>
            <div className="mt-6 flex flex-col gap-2">
              <Link to="/choose-plan" className="w-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] hover:brightness-110 text-white rounded-full py-3 font-black">Riprova</Link>
              <Link to="/dashboard" className="w-full border border-white/20 text-white/80 rounded-full py-3 font-semibold">Vai al Dashboard</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
